import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Globe, CheckCircle2 } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import RelatedLinks from '../components/RelatedLinks';
import TaxCalculator from './TaxCalculator';
import { REGION_PAGES, REGION_LIST } from '../data/regionPages';
import ProductBridge from '../components/ProductBridge';

// Each region guide embeds the calculator for its OWN jurisdiction when one exists,
// so a country page never shows another country's currency/terminology.
const CALC_REGION = {
  'self-employed-tax-scotland': 'UK', 'self-employed-tax-ireland': 'UK',
  'freelancer-tax-germany': 'DE', 'zzp-tax-netherlands': 'UK', 'autonomo-tax-spain': 'ES',
  'auto-entrepreneur-tax-france': 'FR', 'freelancer-tax-italy': 'UK', 'freelancer-tax-portugal': 'UK',
  'self-employed-tax-belgium': 'UK', 'freelancer-tax-switzerland': 'UK', 'self-employed-tax-poland': 'UK',
  'self-employed-tax-australia': 'AU', 'self-employed-tax-new-zealand': 'NZ',
};

// Slugs whose embedded calculator is country-accurate (a dedicated calculator exists),
// so we must NOT show "a dedicated calculator is on the way".
const HAS_DEDICATED_CALC = new Set([
  'freelancer-tax-germany', 'autonomo-tax-spain', 'auto-entrepreneur-tax-france',
  'self-employed-tax-australia', 'self-employed-tax-new-zealand',
]);

export default function RegionTaxLanding({ slug }) {
  const data = REGION_PAGES[slug];
  if (!data) return <Navigate to="/" replace />;

  const path = `/${data.slug}`;
  const others = REGION_LIST.filter((p) => p.slug !== data.slug);
  const calcRegion = CALC_REGION[data.slug] || 'UK';
  const hasDedicatedCalc = HAS_DEDICATED_CALC.has(data.slug);
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'WebPage', name: data.h1, url: `https://moneyfeed.io${path}`, description: data.seoDescription },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: data.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
    { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://moneyfeed.io/' },
      { '@type': 'ListItem', position: 2, name: data.h1, item: `https://moneyfeed.io${path}` },
    ] },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid={`region-tax-landing-${data.slug}`}>
      <Seo title={data.seoTitle} description={data.seoDescription} path={path} jsonLd={jsonLd} />
      <LandingHeader />

      <section className="max-w-4xl mx-auto px-4 sm:px-8 pt-14 pb-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5" style={{ backgroundColor: `${data.accent}1A` }}>
          <Globe className="h-4 w-4" style={{ color: data.accent }} />
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: data.accent }}>{data.badge}</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] leading-tight mb-5">{data.h1}</h1>
        <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl mx-auto">{data.intro}</p>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 pb-6">
        <div className="bg-white rounded-3xl shadow-md p-6 sm:p-8 border border-gray-100">
          <h2 className="text-xl font-bold text-[#064E3B] mb-4">The headline figures</h2>
          <ul className="space-y-3">
            {data.points.map((p, i) => (
              <li key={i} className="flex gap-3 text-gray-700" data-testid={`region-point-${i}`}>
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: data.accent }} />
                <span className="text-sm sm:text-base">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-white rounded-3xl shadow-md p-4 sm:p-6 border border-gray-100">
          <h2 className="text-2xl font-bold text-[#064E3B] text-center mb-1">Quick tax estimate</h2>
          <p className="text-sm text-gray-500 text-center mb-5">{hasDedicatedCalc ? `Estimate your ${data.place} tax below. For the full breakdown, open the dedicated ${data.place} calculator.` : `A general self-employed estimate — pick your country below. Your exact ${data.place} figures are listed above.`}</p>
          <TaxCalculator initialRegion={calcRegion} embedded calcNote={hasDedicatedCalc ? '' : `MoneyFeed doesn't have a dedicated ${data.place} calculator yet — this gives a general estimate. Use the accurate ${data.place} rates above for your filing.`} />
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 pb-4">
        <div className="bg-[#064E3B] rounded-3xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Keep {data.place} taxes tax-ready — on WhatsApp</h2>
          <p className="text-[#A7F3D0] text-sm mb-5 max-w-lg mx-auto">Text your income & expenses, snap receipts, and MoneyFeed keeps categorized, tax-ready books and reports you can export any time.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={data.ctaHref}>
              <Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="region-cta">
                {data.ctaLabel} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            <Link to="/auth">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold rounded-full px-7 py-6 text-base" data-testid="region-signup">Get started free</Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-8">
        <h2 className="text-2xl font-bold text-[#064E3B] text-center mb-8">{data.place} freelancer tax — FAQ</h2>
        <div className="space-y-4">
          {data.faqs.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`region-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 text-center mt-6">Figures are current headline guidance for the self-employed, verified against official sources — not tax advice. Check current rules or an accountant for your situation.</p>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <h2 className="text-lg font-bold text-[#064E3B] mb-4 text-center">Self-employed tax in other places</h2>
        <div className="flex flex-wrap justify-center gap-2">
          {others.map((o) => (
            <Link key={o.slug} to={`/${o.slug}`} className="px-4 py-2 bg-white rounded-full text-sm font-semibold text-[#064E3B] shadow-sm border border-gray-200 hover:border-[#84CC16]" data-testid={`region-link-${o.slug}`}>
              {o.place}
            </Link>
          ))}
        </div>
      </section>

      <ProductBridge intent="tax" />

      <RelatedLinks />

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/whatsapp-bookkeeping" className="hover:text-[#064E3B]">WhatsApp Bookkeeping</Link>
          <Link to="/blog" className="hover:text-[#064E3B]">Blog</Link>
          <Link to="/auth" className="hover:text-[#064E3B]">Sign in</Link>
        </div>
      </footer>
    </div>
  );
}
